import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Pill, { PillTone } from '../components/ui/Pill';
import Button from '../components/ui/Button';
import { EmptyState, SkeletonRows, ErrorNotice } from '../components/ui/States';
import { fmt, utcStamp, duration, relative, orDash, DASH } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 7;

interface SyncLogRow {
  id: string;
  syncType: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  duration: number | null;
  recordsProcessed: number;
  errorMessage: string | null;
}

interface LogsResponse {
  data: SyncLogRow[];
  pagination: { page: number; total: number; pages: number };
}

const STATUS_TONE: Record<string, PillTone> = {
  completed: 'healthy',
  failed: 'failed',
  running: 'running',
  cancelled: 'planned',
};

const STATUS_LABEL: Record<string, string> = {
  completed: 'Success',
  failed: 'Failed',
  running: 'Running',
  cancelled: 'Cancelled',
};

const TRIGGERS = [
  { key: 'ibm', label: 'Run IBM Cloud sync', note: 'Full directory reconcile', path: '/sync/ibm-directory' },
  { key: 'moodle', label: 'Run Moodle sync', note: 'Courses and completions', path: '/sync/moodle' },
  { key: 'attendance', label: 'Run attendance sync', note: 'Zoom participants', path: '/sync/attendance' },
  { key: 'mentorship', label: 'Run mentorship sync', note: 'Seasons, pairings and sessions', path: '/sync/mentorship' },
];

const SCHEDULE = [
  { job: 'ibm_midnight', desc: 'IBM Cloud Directory full sync', time: '00:00', integration: 'ibmDirectory' },
  { job: 'ibm_noon', desc: 'IBM Cloud Directory full sync', time: '12:00', integration: 'ibmDirectory' },
  { job: 'moodle_evening', desc: 'Moodle courses and completions', time: '18:00', integration: 'moodle' },
  { job: 'attendance_evening', desc: 'Zoom attendance', time: '18:30', integration: 'attendance' },
  { job: 'mentorship_evening', desc: 'Mentorship seasons and sessions', time: '19:00', integration: 'mentorship' },
];

export default function Sync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);

  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [running, setRunning] = useState<string[]>([]);

  const { data, isLoading, error } = useQuery<LogsResponse>(
    ['sync-logs', page],
    async () => {
      const res = await api.get('/sync/logs', { params: { page, limit: PAGE_SIZE } });
      return { data: res.data.data, pagination: res.data.pagination };
    },
    { keepPreviousData: true },
  );

  const { data: syncStatus } = useQuery('sync-status', async () => {
    const res = await api.get('/sync/status');
    return res.data.data as Record<string, { status?: string }>;
  });

  const { data: webhook } = useQuery('webhook-health', async () => {
    const res = await api.get('/analytics/dashboard');
    return res.data.data.integrations.webhook as { at: string | null; formId: string | null };
  });

  const trigger = useMutation(
    async ({ key, path }: { key: string; path: string }) => {
      setRunning((r) => [...r, key]);
      return api.post(path);
    },
    {
      onSettled: (_data, _err, vars) => {
        setRunning((r) => r.filter((k) => k !== vars.key));
        queryClient.invalidateQueries('sync-logs');
      },
    },
  );

  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const pages = data?.pagination.pages ?? 1;

  return (
    <>
      <PageHeader
        title="Sync & Integrations"
        sub="Manual triggers, the standing schedule, and the full audit trail. All times UTC."
      />

      {error ? <ErrorNotice>Could not load the sync audit trail.</ErrorNotice> : null}

      <Panel className="mb-6">
        <PanelHeader title="Manual triggers" note={mutable ? undefined : 'Disabled for viewer role'} />
        <PanelBody>
          <div className="grid grid-cols-4 gap-[10px]">
            {TRIGGERS.map((t) => {
              const busy = running.includes(t.key);
              return (
                <button
                  key={t.key}
                  disabled={!mutable || !t.path || busy}
                  onClick={() => t.path && trigger.mutate({ key: t.key, path: t.path })}
                  className="border border-line-strong bg-white p-[14px] rounded-sm hover:bg-selected disabled:opacity-45 disabled:hover:bg-white"
                >
                  <span className="font-semibold block text-small">{busy ? 'Running…' : t.label}</span>
                  <span className="block text-small text-ink-2 mt-1">{t.note}</span>
                </button>
              );
            })}
          </div>
        </PanelBody>
      </Panel>

      <div className="grid gap-6 items-start" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
        <Panel className="min-w-0">
          <PanelHeader title="Recent sync runs" note="SyncLog · newest first" />
          {isLoading ? (
            <SkeletonRows rows={7} widths={['18%', '16%', '10%', '10%', '12%']} />
          ) : rows.length === 0 ? (
            <EmptyState title="No sync runs yet">
              Nothing has run against this database. Trigger a sync above, or wait for the next scheduled window.
            </EmptyState>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-paper border-b border-line">
                  <th className="font-semibold text-small text-ink-2 text-left px-5 py-[10px]">Job</th>
                  <th className="font-semibold text-small text-ink-2 text-left px-[14px] py-[10px]">Started</th>
                  <th className="font-semibold text-small text-ink-2 text-right px-[14px] py-[10px]">Duration</th>
                  <th className="font-semibold text-small text-ink-2 text-right px-[14px] py-[10px]">Records</th>
                  <th className="font-semibold text-small text-ink-2 text-left px-5 py-[10px]">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const failed = r.status === 'failed';
                  const open = expanded === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        onClick={() => failed && setExpanded(open ? null : r.id)}
                        className={`border-b border-line ${failed ? 'cursor-pointer' : ''} ${
                          open ? 'bg-warning-tint' : failed ? 'bg-warning-tint' : ''
                        }`}
                      >
                        <td className="px-5 py-3">
                          <span className="font-mono text-small font-semibold">{r.syncType}</span>
                        </td>
                        <td className="px-[14px] py-3 text-table tabular">{utcStamp(r.startedAt)}</td>
                        <td className="px-[14px] py-3 text-table text-right tabular">{duration(r.duration)}</td>
                        <td className="px-[14px] py-3 text-table text-right tabular">{fmt(r.recordsProcessed ?? 0)}</td>
                        <td className="px-5 py-3">
                          <Pill tone={STATUS_TONE[r.status] ?? 'planned'}>{STATUS_LABEL[r.status] ?? r.status}</Pill>
                        </td>
                      </tr>
                      {open ? (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <div className="bg-warning-tint border-l-4 border-warning px-5 py-4 font-mono text-small leading-[1.6] break-all">
                              {r.errorMessage ?? 'No error detail was recorded for this run.'}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {rows.length > 0 ? (
            <div className="flex items-center justify-between px-5 py-[14px] border-t border-line-strong">
              <span className="font-semibold text-small text-ink-2 tabular">
                Showing {rows.length} of {fmt(total)} runs
              </span>
              <div className="flex gap-2">
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Schedule" note="UTC" />
            <PanelBody className="pt-1">
              {SCHEDULE.map((s) => (
                <div
                  key={s.job}
                  className="flex items-center justify-between gap-4 py-3 border-b border-line last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-small">{s.job}</div>
                    <div className="text-small text-ink-2 mt-[2px]">{s.desc}</div>
                  </div>
                  {syncStatus?.[s.integration]?.status === 'disabled' ? (
                    <Pill tone="planned">Not configured</Pill>
                  ) : s.time ? (
                    <span className="font-semibold tabular text-table">{s.time}</span>
                  ) : (
                    <span className="text-ink-2">{DASH}</span>
                  )}
                </div>
              ))}
            </PanelBody>
          </Panel>

          <div className="border-t border-line-strong pt-6">
            <h2 className="font-serif font-semibold text-heading text-ink">Gravity webhook</h2>
            <div className="font-mono text-small text-accent mt-2">POST /api/webhooks/gravity</div>
            <div className="border-t border-line my-4" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="font-semibold text-small text-ink-3">Last event</div>
                <div className="text-table text-ink mt-1">{relative(webhook?.at)}</div>
              </div>
              <div>
                <div className="font-semibold text-small text-ink-3">Signature</div>
                <div className="text-table text-accent mt-1">HMAC OK</div>
              </div>
            </div>
            <p className="text-small text-ink-3 mt-4">
              Form {orDash(webhook?.formId)} · recency is the health signal — a quiet webhook is the first sign
              registrations have stopped arriving.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
